import type { Session as SupabaseSession, User as SupabaseUser } from '@supabase/supabase-js';

import { ENV } from '@/shared/config/env';
import { supabase } from '@/shared/lib/supabase';
import type {
  ErrorResponse,
  GetMeResponse,
  MagicLinkApiResponse,
  MagicLinkRequest,
  UpdateProfileResponse,
} from '@/shared/types/auth';

import { apiClient } from './client';

export class AuthService {
  constructor() {
    // baseURL is set in the ENV configuration
  }

  /**
   * Get current user information
   * GET /auth/me
   * @param skipInterceptor - Skip the auth interceptor refresh logic (used during auth initialization)
   */
  async getMe(skipInterceptor: boolean = false): Promise<GetMeResponse> {
    try {
      const config = skipInterceptor ? { headers: {}, _skipInterceptor: true } : {};
      const response = await apiClient.get<GetMeResponse>('/auth/me', config);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get current user information with a specific token
   * GET /auth/me
   * @param token - The access token to use for authentication
   */
  async getMeWithToken(token: string): Promise<GetMeResponse> {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        _skipInterceptor: true,
      };
      const response = await apiClient.get<GetMeResponse>('/auth/me', config);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Send magic link for authentication
   * POST /auth/magic-link
   */
  async sendMagicLink(data: MagicLinkRequest): Promise<MagicLinkApiResponse> {
    try {
      const response = await apiClient.post<MagicLinkApiResponse>('/auth/magic-link', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Send OTP code via email using Supabase
   */
  async sendEmailOTP(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          // Explicitly request email change type to get a 6-digit code
          emailRedirectTo: undefined, // Ensure no redirect URL is set
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Verification code sent to your email!',
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to send OTP');
    }
  }

  /**
   * Verify OTP code using Supabase
   */
  async verifyEmailOTP(
    email: string,
    token: string,
  ): Promise<{ user: SupabaseUser; session: SupabaseSession }> {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.user || !data.session) {
        throw new Error('Invalid verification code');
      }

      // Manually set the session to ensure persistence
      if (data.session) {
        // Logging removed - session data logged in supabaseAuthService
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      return {
        user: data.user,
        session: data.session,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to verify OTP');
    }
  }

  /**
   * Sign in with Google OAuth using Supabase
   */
  async signInWithGoogle(): Promise<{ success?: boolean; error?: string }> {
    try {
      const redirectUrl = ENV.APP_URL || window.location.origin;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to sign in with Google',
      };
    }
  }

  /**
   * Register/create user after OAuth login
   * POST /auth/register
   */
  async registerOAuthUser(): Promise<GetMeResponse> {
    try {
      const response = await apiClient.post<GetMeResponse>('/auth/register', {});
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Update user profile settings
   * PATCH /user/profile
   */
  async updateProfile(data: { autoRefillEnabled?: boolean }): Promise<UpdateProfileResponse> {
    try {
      const response = await apiClient.patch<UpdateProfileResponse>('/user/profile', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Delete user account permanently
   * POST /user/unregister
   */
  async unregisterUser(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/user/unregister', {});

      // Check if response is successful
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          message: 'Account deleted successfully',
        };
      } else {
        return {
          success: false,
          message: 'Failed to delete account',
        };
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = (error as { response: { data: ErrorResponse } }).response.data;
        throw new Error(errorResponse.message || 'Failed to delete account');
      }
      throw new Error('Failed to delete account');
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
